import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Card, Typography } from '@mui/material';
import NotificationModal from './NotificationModal';
import { createNotificationApi, deleteNotification, getAllNotificationApi, notificationByIdApi, updateNotification } from '../../api';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import dayjs from 'dayjs';
import { AdminTable, ActionCell } from '../../common/adminTable';
import Paginations from '../../common/pagination/Paginations';
import StatusChip from '../../common/statusChip/StatusChip';
import configuration from '../../config/Config';
import ReyLeaf from '../../common/assests/reyleafLogo.svg';
import UserAvatar from '../../common/avatar/UserAvatar';

const rowsPerPage = 10;

const PushNotification = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();

  const [open, setOpen] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(false);
  const [notificationByIdData, setNotificationByIdData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleAllNotification = async () => {
    try {
      setLoading(true);
      const response = await getAllNotificationApi({ token, currentPage });
      setNotificationData(response?.data || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error", error);
    }
  };

  const handleDeleteNotification = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Notification",
      description: "Are you sure you want to delete this notification?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!isConfirmed) return;
    setDeletingId(id);
    try {
      const response = await deleteNotification({ token, id });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occurred" : error?.error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddNotification = async (data) => {
    try {
      if (!data.notificationDate || !data?.time) {
        toast.warning("Please select a Notification Date and Time.");
        return;
      }
      if (data.selectedType1 === "Notification" && !data.picture) {
        toast.warning("Please select a Image");
        return;
      }
      if (data.selectedType1 === "Notification" && (!data.title || !data.description)) {
        toast.warning("Title and Description are required for Notifications.");
        return;
      }
      if (data.selectedType === "Selected Users" && (!data.recipients || data.recipients.length === 0)) {
        toast.warning("Please select at least one user/vender.");
        return;
      }
      if (data.selectedType1 === "Email" && !data.description) {
        toast.warning("Please provide a Description for the email.");
        return;
      }

      const formData = new FormData();
      formData.append('sendTo', data.sendTo);
      formData.append('notificationDate', dayjs(data.notificationDate).format('YYYY-MM-DD'));
      formData.append('notificationTime', data.time || '00:00:00');
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('sendNotification', data.selectedType);
      formData.append('notificationType', data.selectedType1);
      if (data.selectedType === 'Selected Users') {
        formData.append('recipients', JSON.stringify(data.recipients));
      }
      if (data.picture) {
        formData.append('picture', data.picture);
      }
      const response = await createNotificationApi({ token, formData });
      toast.success(response?.message);
      setChange(!change);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? 'Make Sure date and time are properly filled !' : error?.error);
    }
  };

  const handleEditNotification = async (id) => {
    try {
      setLoading(true);
      const response = await notificationByIdApi({ token, id });
      setNotificationByIdData(response?.data || {});
      setLoading(false);
      setOpen(true);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleUpdateNotification = async (data) => {
    if (!data.notificationDate || !data?.time) {
      toast.warning("Please select Notification date and new time.");
      return;
    }
    if (data.selectedType1 === "Notification" && (!data.title || !data.description)) {
      toast.warning("Title and Description are required for Notifications.");
      return;
    }
    if (data.selectedType === "Selected Users" && (!data.recipients || data.recipients.length === 0)) {
      toast.warning("Please select at least one user/vender.");
      return;
    }
    if (data.selectedType1 === "Email" && !data.description) {
      toast.warning("Please provide a Description for the email.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('sendTo', data.sendTo);
      formData.append('notificationDate', dayjs(data.notificationDate).format('YYYY-MM-DD'));
      formData.append('notificationTime', data.time || '00:00:00');
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('sendNotification', data.selectedType);
      formData.append('notificationType', data.selectedType1);
      if (data.selectedType === 'Selected Users') {
        formData.append('recipients', JSON.stringify(data.recipients));
      }
      if (data.picture) {
        formData.append('picture', data.picture);
      }
      const response = await updateNotification({ token, id: notificationByIdData?._id, formData });
      toast.success(response?.message);
      setChange(!change);
      setOpen(false);
    } catch (error) {
      toast.error(error?.error || 'An error occurred.');
    }
  };

  useEffect(() => {
    handleAllNotification();
  }, [currentPage, change]);

  const handleOpenModal = (data) => {
    setNotificationByIdData(data);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setNotificationByIdData(null);
  };

  const columns = [
    {
      field: "_srNo",
      headerName: "Sr. No",
      width: 70,
      renderCell: (r) => r._srNo,
    },
    { field: "sendTo", headerName: "Send To", width: 120, renderCell: (r) => r.sendTo || "-" },
    { field: "title", headerName: "Notification Title", minWidth: 160, renderCell: (r) => r.title || "-" },
    { field: "notificationType", headerName: "Type", width: 110, renderCell: (r) => r.notificationType || "-" },
    {
      field: "notificationPicture",
      headerName: "Image",
      width: 80,
      renderCell: (r) => (
        <UserAvatar
          src={r.notificationPicture ? `${configuration.cdnFront}/${r.notificationPicture}` : undefined}
          name={r.title}
          size={40}
        />
      ),
    },
    { field: "status", headerName: "Status", width: 100, renderCell: (r) => <StatusChip value={r?.status} /> },
    {
      field: "notificationDate",
      headerName: "Push Date",
      width: 110,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) => r.notificationDate ? new Date(r.notificationDate).toLocaleDateString() : "-",
    },
    {
      field: "__actions",
      headerName: "",
      width: 90,
      align: "right",
      renderCell: (r) => (
        <ActionCell
          actions={[
            {
              label: "Edit",
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEditNotification(r._id),
            },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDeleteNotification(r._id),
              loading: deletingId === r._id,
            },
          ]}
        />
      ),
    },
  ];

  const rows = notificationData.map((item, i) => ({
    ...item,
    _srNo: (currentPage - 1) * rowsPerPage + i + 1,
  }));

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginBottom: "10px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#362B24" }}>
            All Push Notification
          </Typography>
          <Button
            onClick={() => handleOpenModal(null)}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: '29px',
              background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
              boxShadow: '1px 2px 30px 0px rgba(0, 0, 0, 0.06)',
              height: '50px',
              color: 'white',
              textTransform: 'none',
              px: 3,
              "&:hover": { opacity: 0.9 },
            }}
          >
            Create New Notification
          </Button>
        </Box>

        <AdminTable
          columns={columns}
          rows={rows}
          loading={loading}
          rowKey="_id"
          emptyText="No notifications found"
          deletingId={deletingId}
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <NotificationModal
        open={open}
        onClose={handleCloseModal}
        editingData={notificationByIdData}
        onSave={notificationByIdData ? handleUpdateNotification : handleAddNotification}
      />
    </div>
  );
};

export default PushNotification;
